// Degree Angle
// Angle > +45°   → Strong bullish trend
// Angle 20–45°  → Healthy uptrend
// Angle 5–20°   → Weak / accumulation
// Angle -5–5°   → Sideways

import { isConsecutiveDay } from "./constants";

// Angle < -20°  → Strong downtrend
export const degAngle = (oldPrice, newPrice, bars) => {
  if (!oldPrice || oldPrice <= 0 || !bars || bars <= 0) return 0;
  const changePercentPerDay = ((newPrice - oldPrice) / oldPrice) * 100 / bars;
  const radians = Math.atan(changePercentPerDay);
  const degree = (radians * 90) / Math.PI;

  return Number(degree.toFixed(2));
};

/*
0–15°	    Sideways / weak
15–30°	  Healthy trend
30–45°	  Strong trend
>45°	    Overextended
*/
export const slopeAngle = (stock) => {
  const stockArray = Array.isArray(stock) ? stock : stock ? [stock] : [];  
  const base = stockArray[0];
  const normalized = stockArray.map(p => ((p - base) / base) * 100);
 
  const {slope} = linearRegression(normalized);
  const radians = Math.atan(slope);
  const degree = radians * (180 / Math.PI);
  return Number(degree.toFixed(2));
};


// ############  Candles types ################################
export const isBullishCandle = (candle) => {
  if (!candle) return false;
  const { open_price, close_price } = candle;
  return close_price > open_price; // green candle
};

export const bodySize = (candle) => Math.abs(candle.close_price - candle.open_price);

export const isDragonflyDoji = (candle) =>{
    const range = candle.high_price - candle.low_price;
  if (range <= 0) {
    return false
  }

  
  const upperWickPct = ((candle.high_price - candle.close_price) / range) * 100;
  const bodyPctRange = ((candle.close_price - candle.open_price) / range) * 100;
  const lowerWickPct = ((candle.open_price - candle.low_price) / range) * 100;

  return (
    upperWickPct < 5 &&
    bodyPctRange >= 10 &&
    lowerWickPct <= 80
  );
}


export const isBullishEngulfing = (candles) => {
  if (!candles || candles.length < 2) return false;

  const prev = candles[1];
  const curr = candles[0];

  const prevOpen = Number(prev.high_price);
  const prevClose = Number(prev.low_price);
  const currOpen = Number(curr.open_price);
  const currClose = Number(curr.close_price);
  const currHigh = Number(curr.high_price);
  const currLow  = Number(curr.low_price);

  const prevVolume = Number(prev.delivery_qty || 0);
  const currVolume = Number(curr.delivery_qty || 0);

  // Candle range
  const range = currHigh - currLow;
  if (range <= 0) return false;

  // Wick percentages
  const upperWickPct = (currHigh - currClose) / range;
  const lowerWickPct = (currOpen - currLow) / range;

  return (
    // Engulfing conditions
    prev.chn_per < 0 &&
    curr.chn_per > 0 &&
    prevClose < prevOpen &&             // previous bearish
    currClose > currOpen &&             // current bullish
    currOpen <= prevClose &&            // engulf open
    currClose >= prevOpen &&            // engulf close

    // Strong bullish close (near high)
    upperWickPct <= 0.10 &&              // high ≈ close
    lowerWickPct <= 0.10 &&              // low ≈ open

    // Volume confirmation
    currVolume > prevVolume
  );
};


export const isMorningStar = (candles, index) => {
  if (index < 2) return false;

  const c1 = candles[index - 2]; // bearish
  const c2 = candles[index - 1]; // small body
  const c3 = candles[index];     // bullish

  const body1 = Math.abs(c1.open_price - c1.close_price);
  const body2 = Math.abs(c2.open_price - c2.close_price);
  const body3 = Math.abs(c3.open_price - c3.close_price);

  // 1️⃣ First candle: strong bearish
  const isBearish1 = c1.close_price < c1.open_price && body1 / c1.open_price > 0.01;

  // 2️⃣ Second candle: small body (indecision)
  const isSmallBody2 = body2 < body1 * 0.4;

  // 3️⃣ Third candle: strong bullish
  const isBullish3 = c3.close_price > c3.open_price && body3 / c3.open_price > 0.01;

  // 4️⃣ Confirmation: closes above 50% of first candle
  const midpoint1 = (parseFloat(c1.open_price) + parseFloat(c1.close_price)) / 2;
  // const midpoint1 = c1.close_price / 2;
  const closesAboveMid = c3.close_price > midpoint1;

  return (
    isBearish1 &&
    isSmallBody2 &&
    isBullish3 &&
    closesAboveMid
  );
};

// ############## END of Candle Types ####################


// ############## For Get Trends Types ####################
export const getTrend = (prices, thresholdPercent = 2) => {

  if (!Array.isArray(prices) || prices.length < 2) {
    return {
      'trend': "Not Valid Data",
      'chnper': 0
    };
  }

  const first = prices[0]; // latest
  const last = prices[prices.length - 1]; // oldest

  if (first === 0) {
    return {
      'trend': "Not Valid Data",
      'chnper': 0
    };
  }

  const changePercent = ((first - last) / last) * 100;
  // console.log("Price", prices, changePercent, thresholdPercent)

  if (changePercent > thresholdPercent) {
    return {
      'trend': "Uptrend",
      'chnper': changePercent.toFixed(2)
    };
  } else if (changePercent < -(thresholdPercent - 3)) {
    return {
      'trend': "Downtrend",
      'chnper': changePercent.toFixed(2)
    };
  } else {
    return {
      'trend': "Sideways",
      'chnper': changePercent.toFixed(2)
    };
  }
}



export const hasBullishRunPattern = (stockData, minConsecutive = 2) => {
  stockData.reverse();
  const recent = stockData.slice(stockData.length - (minConsecutive + 2));

  let consec = 0;
  for (let i = 1; i < recent.length; i++) {
    const prev = recent[i - 1];
    const cur = recent[i];
    
    const green = isBullishCandle(cur);
    // const green = cur.chn_per > 1.5;
    const higherHigh = cur.high_price > prev.high_price;
    const higherLow = cur.low_price > prev.low_price;

    const curBody = bodySize(cur);
    const prevBody = bodySize(prev);
    const decentBody = curBody > 1  && curBody >= prevBody * 0.5; // avoid tiny doji

    if (green && decentBody && higherHigh && higherLow) {// && higherLow && decentBody 
      consec += 1;
      if (consec >= minConsecutive) return true;
    } else {
      consec = 0;
    }
  }

  return false;
};

// ################ Check true Volumes > 2,00,000

export const checkTrueVolumes = (data, threshold = 200000) =>{
    let trueAverageCount = 0;
    const volumes = data.map((d) => parseInt(d.ttl_trd_qty, 10));

    volumes.forEach((v, i) => {
      if (v >= threshold) {
        trueAverageCount = trueAverageCount + 1;
      }
    });
     return trueAverageCount >= volumes.length - 3;
}


// ############### linearRegression ###########################
export const linearRegression = (values) =>{ 
// function linearRegression(values) {
const n = values.length;
if (n === 0) return { slope: 0, intercept: 0 };
let sumX = 0, sumY = 0, sumXY = 0, sumXX = 0;
for (let i=0;i<n;i++){
let x = i;
let y = values[i];


sumX += x; sumY += y; sumXY += x*y; sumXX += x*x;
}
let denom = n*sumXX - sumX*sumX;


if (denom === 0) return { slope: 0, intercept: sumY/n };
const slope = (n*sumXY - sumX*sumY)/denom;
const intercept = (sumY - slope*sumX)/n;
return { slope, intercept };
}

//###################  btstPredict
export const btstPredict = (prices) => {
  if (!prices || prices.length < 5) {
    return { bias: "NO TRADE" };
  }

  const series = [...prices].reverse(); 
  // console.log("Price", series);

  const { slope, intercept } = linearRegression(series);
  const n = series.length;

  const todayReg = slope * (n - 1) + intercept;
  const tomorrowReg = slope * n + intercept;

  const latestClose = series[n - 1];

  const slopePct = slope / latestClose;

  if (
    slopePct > 0.003 &&              // ~0.3% daily trend
    latestClose > todayReg           // price above regression
  ) {
    return {
      bias: "BULLISH",
      price: Number(todayReg.toFixed(2)),
      nextday: Number(tomorrowReg.toFixed(2)),
      target: Number((todayReg + (todayReg * 5/100)).toFixed(2)),
      stopLoss: Number((todayReg - (todayReg * 2/100)).toFixed(2)),
    };
  }

  return { bias: "NO TRADE" };
};


// ################### detectNR7WithTrend
export const detectNR7WithTrend = (stockData) => {
  const candle = 8;
  if (!stockData || stockData.length < candle) {
    return false;
  }

  // Take last 8 candles
  const consolDays = stockData.slice(0, candle).reverse();
  

  // Split candles
  const nrCandles = consolDays.slice(0, candle-1); // day 0–7
  const breakoutCandle = consolDays[candle-1]; // 8th candle [Today]
  const seventhCandle = consolDays[candle-2];// 7th candle     
  const sixthCandle = consolDays[candle-3];// 6th candle     
  const fifthCandle = consolDays[candle-4];// 5th candle     

  const ranges = nrCandles.map(d => parseFloat(d.high_price) - parseFloat(d.low_price));
  const minRange = Math.min(...ranges);
  const maxRange = Math.max(...ranges);

  
  const highestHigh = Math.max(...nrCandles.map(d => parseFloat(d?.high_price)));
  const stopLoss = Math.max(...nrCandles.map(d => parseFloat(d?.low_price)));
  const stopLossPer = Math.max(((breakoutCandle.close_price - stopLoss)/stopLoss) * 100, 2);
  const target = parseFloat(breakoutCandle?.high_price) + ((parseFloat(breakoutCandle?.high_price) * (stopLossPer * 2))/100); 
  
  
  const isNR = Math.round(parseFloat(breakoutCandle?.high_price) - parseFloat(breakoutCandle?.low_price)) <= Math.round(maxRange)
               && Math.round(parseFloat(breakoutCandle?.high_price) - parseFloat(breakoutCandle?.low_price)) >= Math.round(minRange);


  const isBreakout =  parseFloat(breakoutCandle?.high_price) > highestHigh
                      && parseFloat(breakoutCandle?.close_price) > parseFloat(breakoutCandle?.open_price) 
                      && parseFloat(seventhCandle?.close_price) > parseFloat(seventhCandle?.open_price)
                      && parseFloat(seventhCandle?.ttl_trd_qty) > parseFloat(sixthCandle?.ttl_trd_qty)
                      && parseFloat(sixthCandle?.ttl_trd_qty) > parseFloat(fifthCandle?.ttl_trd_qty)
                             
  return {
    valid: isNR,
    isBreakout: isBreakout,
    breakoutCandle: breakoutCandle,
    price: parseFloat(breakoutCandle.close_price),
    stopLoss: stopLoss,
    stopLossPer:stopLossPer,
    target: target    
  };
};

// ################### detectAscendingTriangle
export const detectAscendingTriangle = (stockData) =>{
  if (!stockData || stockData.length < 21) {
    return false;
  }
  const noOfDate = stockData.length - 1;
  stockData = stockData.reverse();

  const CONFIG = {
    resistanceTolerancePct: 0.02, // 2% tolerance to cluster highs into 'resistance'
    minResistanceTouches: 3,
    minSupportSlopePct: 0.15, // percent across the lookback (e.g. 0.15% meaning support must rise ~0.15% over window)
    consolidationRangeMaxPct: 6 // max consolidation width percent
};
  

  // build arrays (safe access and normalization)
  const highs = stockData.map(r => parseFloat(r.high_price));
  const lows  = stockData.map(r => parseFloat(r.low_price));
  const closes= stockData.map(r => parseFloat(r.close_price));
  

  const maxHigh = Math.max(...highs);
  

  const tolerance = CONFIG.resistanceTolerancePct * maxHigh;
  const touchIndexes = [];
  highs.forEach((h, i) => {
    if (Math.abs(maxHigh - h) <= tolerance) touchIndexes.push(i);
  });
  const resistanceTouches = touchIndexes.length;
  const resistance = resistanceTouches >= 1
    ? touchIndexes.reduce((s, idx) => s + highs[idx], 0) / resistanceTouches : maxHigh;
  
  const lr = linearRegression(lows);
  const slope = lr.slope; // price per day (absolute)
  const intercept = lr.intercept;
  const supportStart = intercept + slope * 0;
  const supportEnd = intercept + slope * noOfDate;
  const supportSlopePct = (Math.abs(supportStart) < 1 ? 0 : ((supportEnd - supportStart) / Math.abs(supportStart)) * 100);


  // breakout 
  const lastClose = closes[closes.length - 1];
  const breakoutClose = lastClose > resistance? true:false;
    

  // basic structural checks (kept minimal)
  const hasMinTouches = resistanceTouches >= CONFIG.minResistanceTouches;
  const risingSupport = supportSlopePct >= CONFIG.minSupportSlopePct;
  const consolidationRange = Number.isFinite(resistance) ? resistance - supportEnd : NaN;
  const consolidationRangePct = resistance !== 0 ? (consolidationRange / resistance) * 100 : NaN;
  const consolidationTight = Number.isFinite(consolidationRangePct) ? consolidationRangePct <= CONFIG.consolidationRangeMaxPct : false;

  const isBase = hasMinTouches && risingSupport && consolidationTight;
  const isBreakout = isBase && breakoutClose

  return {
    isBreakout,
    breakoutClose,    
  };
}

// ################# Weekly Bullish
const groupToWeekly = (dailyData) => {
  const weeks = {};

  dailyData.forEach(c => {
    const d = new Date(c.delivery_date);
    const year = d.getFullYear();
    const week = Math.ceil(
      ((d - new Date(year, 0, 1)) / 86400000 + new Date(year, 0, 1).getDay() + 1) / 7
    );

    const key = `${year}-W${week}`;

    if (!weeks[key]) {
      weeks[key] = {
        open_price: c.open_price,
        close_price: c.close_price,
        high_price: c.high_price,
        low_price: c.low_price,
        delivery_date: c.delivery_date,
        chn_per: c.chn_per
      };
    } else {
      weeks[key].high_price = Math.max(weeks[key].high_price, c.high_price);
      weeks[key].low_price  = Math.min(weeks[key].low_price, c.low_price);
      weeks[key].close_price = c.close_price;
      weeks[key].chn_per = ((weeks[key].close_price - weeks[key].open_price) / weeks[key].open_price) * 100;
    }
  });

  return Object.values(weeks);
};
export const hasWeeklyBullishRunPattern = (stockData, minConsecutive = 2) => {
  if (!stockData || stockData.length < 10) return false;

  // convert daily → weekly
  const weeklyData = groupToWeekly(stockData);
  

  // latest candle last
  const data = [...weeklyData].reverse();


  // exclude current forming week
  const recent = data.slice(
    data.length - (minConsecutive + 2),
    data.length - 1
  );

  let consec = 0;

  for (let i = 1; i < recent.length; i++) {
    const prev = recent[i - 1];
    const cur = recent[i];

    // const green = cur.chn_per > 2.5;   // stronger weekly move
    // const green = isBullishCandle(cur);
    const higherHigh = cur.high_price > prev.high_price;
    const higherLow = cur.low_price > prev.low_price;

      //higherHigh && higherLow &&
    if (higherHigh && higherLow) {
      consec++;
      if (consec >= minConsecutive) return true;
    } else {
      consec = 0;
    }
  }

  return false;
};



//#################### FVG

export const detectFVG = (data) => {
  let fvgZones = [];
  let bullishCount = 0;
  let bearishCount = 0;

  for (let i = 1; i < data.length - 1; i++) {

    const prev = data[i - 1];
    const curr = data[i];
    const next = data[i + 1];

    // 🔹 Impulse candle body filter (> 3%)
    const bodyPct =  Math.abs(parseFloat(curr.close_price) - parseFloat(curr.open_price)) / parseFloat(curr.open_price);

    if (bodyPct < 0.03) continue;

    // 🔹 Bullish FVG
    if (parseFloat(next.low_price) > parseFloat(prev.high_price)) {
      bullishCount++;

      fvgZones.push({
        type: "BULLISH",
        from: parseFloat(curr.high_price),
        to: parseFloat(curr.low_price),
        date: curr.delivery_date,
        // impulseCandle: i,
        // bodyPct: bodyPct        
      });
    }

    // 🔹 Bearish FVG
    if (parseFloat(next.high_price) < parseFloat(prev.low_price)) {
      bearishCount++;

      fvgZones.push({
        type: "BEARISH",
        from: parseFloat(curr.high_price),
        to: parseFloat(curr.low_price),
        date: curr.delivery_date,
        // impulseCandle: i,
        // bodyPct: bodyPct        
      });
    }
  }

  return {
    totalFVG: fvgZones.length,
    bullishCount,
    bearishCount,
    fvgZones
  };
};



export const detectBearishFVGReversal = (data) => {
  if (data.length < 16) return null;

  // Convert CURRENT → OLD  →  OLD → NEW
  const candles = [...data].slice(1, 15).reverse();
  

  const result = detectFVG(candles);
  

  // 🔹 Condition 1: Minimum bearish FVGs
  if (result.bearishCount <= 2) return null;

  // 🔹 Get first (oldest) bearish FVG
  const bearishFVGs = result.fvgZones
    .filter(z => z.type === "BEARISH") //BULLISH || BEARISH
    .sort((a, b) => new Date(a.date) - new Date(b.date));


    let consecutiveBullishFVGs = [];
    for (let i = 1; i < bearishFVGs.length; i++) {
      if (isConsecutiveDay(bearishFVGs[i - 1].date, bearishFVGs[i].date)) {
        consecutiveBullishFVGs.push(bearishFVGs[i - 1], bearishFVGs[i]);
      }
    }

    if(consecutiveBullishFVGs.length < 2) return null;


  return {
    setup: "BEARISH_FVG_REVERSAL",
    target: consecutiveBullishFVGs[0].from,
    sl: consecutiveBullishFVGs[consecutiveBullishFVGs.length - 1].to,
    zones: result.fvgZones,
    candles: `${candles[0].delivery_date} - ${candles[candles.length - 1].delivery_date}`
  };
};


// ######################################################


export const findSLorTarget = (data, startDate, sl, target) => {
  // step 1: filter from start date
  const filtered = data
    .filter(d => d.delivery_date > startDate)
    .sort((a, b) => new Date(a.delivery_date) - new Date(b.delivery_date)); // OLD → NEW

  for (const candle of filtered) {
    const low = Number(candle.low_price);
    const high = Number(candle.high_price);

    if (low <= sl) {
      return {
        result: "SL HIT",
        hit_price: sl,
        hit_date: candle.delivery_date,
        candle,
      };
    }

    if (high >= target) {
      return {
        result: "TARGET HIT",
        hit_price: target,
        hit_date: candle.delivery_date,
        candle,
      };
    }
  }

  return {
    result: "NONE",
    message: "Neither SL nor Target hit",
  };
}


export const percent = (currPrice, targetPrice) =>
  Number(
    (((Number(targetPrice) - Number(currPrice)) * 100) / Number(currPrice))
      .toFixed(2)
  );                    


// Calculate Trend (20-day optimized, CLOSE-PRICE BASED) need to be removed
// export const calculateTrend2 = (stocks) => {
//   if (!Array.isArray(stocks) || stocks.length < 20) {
//     return { trendType: "SIDEWAYS", slope: 0, angle: 0, line: [] };
//   }

//   /* ---------------------------------------
//      STEP 0: Normalize data (OLD → NEW)
//   --------------------------------------- */
//   const data = [...stocks]
//     .sort((a, b) => new Date(a.delivery_date) - new Date(b.delivery_date))
//     .slice(-20)
//     .map(d => ({
//       close: Number(d.close_price),
//       high: Number(d.high_price),
//       low: Number(d.low_price)
//     }));

//   /* ---------------------------------------
//      STEP 1: Determine trend direction
//      (structure confirmation only)
//   --------------------------------------- */
//   const lastClose = data[19].close;
//   const midClose  = data[14].close;
//   const firstClose = data[0].close;

//   let trendType = "SIDEWAYS";

//   if (lastClose > midClose && midClose > firstClose) {
//     trendType = "UPTREND";
//   } else if (lastClose < midClose && midClose < firstClose) {
//     trendType = "DOWNTREND";
//   }

//   /* ---------------------------------------
//      STEP 2: Linear regression on CLOSE PRICE
//      (this is the KEY FIX)
//   --------------------------------------- */
//   const n = data.length;

//   let sumX = 0, sumY = 0, sumXY = 0, sumX2 = 0;

//   for (let i = 0; i < n; i++) {
//     const x = i;
//     const y = data[i].close;

//     sumX += x;
//     sumY += y;
//     sumXY += x * y;
//     sumX2 += x * x;
//   }

//   const denominator = n * sumX2 - sumX * sumX;

//   if (denominator === 0) {
//     return { trendType: "SIDEWAYS", slope: 0, angle: 0, line: [] };
//   }

//   const slope = (n * sumXY - sumX * sumY) / denominator;
//   const intercept = (sumY - slope * sumX) / n;

//   /* ---------------------------------------
//      STEP 3: Angle
//   --------------------------------------- */
//   const angle = +(Math.atan(slope) * (180 / Math.PI)).toFixed(2);

//   /* ---------------------------------------
//      STEP 4: Sideways filter (VERY IMPORTANT)
//   --------------------------------------- */
//   if (Math.abs(angle) < 8) {
//     return { trendType: "SIDEWAYS", slope: 0, angle: 0, line: [] };
//   }

//   /* ---------------------------------------
//      STEP 5: Trend line (chart-ready)
//   --------------------------------------- */
//   const trendLine = data.map((_, i) => slope * i + intercept);

//   return {
//     trendType,
//     slope: +slope.toFixed(4),
//     angle,
//     line: trendLine
//   };
// };


export const analyzeMarket = (stocks) => {
  /*
| Confidence | Meaning                   |
| ---------- | ------------------------- |
| 0–40       | Noise / Range             |
| 40–60      | Weak trend                |
| 60–75      | Tradable                  |
| 75–100     | Institutional-grade trend |

  */

  if (!Array.isArray(stocks) || stocks.length < 20) {
    return { trend: "SIDEWAYS", structure: "INSUFFICIENT_DATA", confidence: 0 };
  }

  /* ---------------------------------------
     STEP 0: Normalize (OLD → NEW)
  --------------------------------------- */
  const candles = [...stocks]
    .sort((a, b) => new Date(a.delivery_date) - new Date(b.delivery_date))
    .slice(-20)
    .map(d => ({
      close: +d.close_price,
      high: +d.high_price,
      low: +d.low_price
    }));

  /* ---------------------------------------
     STEP 1: Swing Detection
  --------------------------------------- */
  const swingHighs = [];
  const swingLows = [];

  for (let i = 2; i < candles.length - 2; i++) {
    const { high, low } = candles[i];

    if (
      high > candles[i - 1].high &&
      high > candles[i - 2].high &&
      high > candles[i + 1].high &&
      high > candles[i + 2].high
    ) swingHighs.push({ price: high, index: i });

    if (
      low < candles[i - 1].low &&
      low < candles[i - 2].low &&
      low < candles[i + 1].low &&
      low < candles[i + 2].low
    ) swingLows.push({ price: low, index: i });
  }

  if (swingHighs.length < 2 || swingLows.length < 2) {
    return { trend: "SIDEWAYS", structure: "RANGE", confidence: 20 };
  }

  const H1 = swingHighs.at(-2).price;
  const H2 = swingHighs.at(-1).price;
  const L1 = swingLows.at(-2).price;
  const L2 = swingLows.at(-1).price;
  const lastClose = candles.at(-1).close;

  /* ---------------------------------------
     STEP 2: Market Structure
  --------------------------------------- */
  let structure = "RANGE";

  if (H2 > H1 && L2 > L1) structure = "UPTREND";
  else if (H2 < H1 && L2 < L1) structure = "DOWNTREND";

  if (structure !== "UPTREND" && lastClose > H1) {
    structure = "BULLISH_CHOCH";
  }

  /* ---------------------------------------
     STEP 3: Linear Regression
  --------------------------------------- */
  const n = candles.length;
  let sumX = 0, sumY = 0, sumXY = 0, sumX2 = 0;

  candles.forEach((c, i) => {
    sumX += i;
    sumY += c.close;
    sumXY += i * c.close;
    sumX2 += i * i;
  });

  const slope = (n * sumXY - sumX * sumY) / (n * sumX2 - sumX ** 2);
  const angle = +(Math.atan(slope) * 180 / Math.PI).toFixed(2);

  /* ---------------------------------------
     STEP 4: Trend & Strength Label
  --------------------------------------- */
  let trend = "SIDEWAYS";
  if (Math.abs(angle) > 8) trend = slope > 0 ? "BULLISH" : "BEARISH";

  let strength = "NO_TRADE";
  const absAngle = Math.abs(angle);

  if (absAngle > 8 && absAngle <= 15) strength = "WEAK";
  if (absAngle > 15 && absAngle <= 25) strength = "MODERATE";
  if (absAngle > 25 && absAngle <= 40) strength = "STRONG";
  if (absAngle > 40 || structure === "BULLISH_CHOCH") strength = "EXPLOSIVE";

  /* ---------------------------------------
     STEP 5: CONFIDENCE (0–100)
  --------------------------------------- */
  let confidence = 0;

  // Structure alignment
  if (structure === "UPTREND" && trend === "BULLISH") confidence += 35;
  if (structure === "DOWNTREND" && trend === "BEARISH") confidence += 35;
  if (structure === "BULLISH_CHOCH") confidence += 25;

  // Trend direction
  if (trend !== "SIDEWAYS") confidence += 25;

  // Angle strength
  confidence += Math.min(20, Math.abs(angle));

  // Strength label weight
  if (strength === "WEAK") confidence += 5;
  if (strength === "MODERATE") confidence += 10;
  if (strength === "STRONG") confidence += 15;
  if (strength === "EXPLOSIVE") confidence += 20;

  confidence = Math.min(100, Math.round(confidence));

  /* ---------------------------------------
     STEP 6: ENTRY / SL / TP
  --------------------------------------- */
  let trade = null;

  if (trend === "BULLISH" && ["UPTREND", "BULLISH_CHOCH"].includes(structure)) {
    const entry = L2;
    const stopLoss = L2 - (H2 - L2) * 0.25;
    const risk = entry - stopLoss;

    trade = {
      direction: "BUY",
      entry: +entry.toFixed(2),
      stopLoss: +stopLoss.toFixed(2),
      takeProfit1: +H2.toFixed(2),
      takeProfit2: +(entry + risk * 2).toFixed(2),
      rr: "1:2"
    };
  }

  if (trend === "BEARISH" && structure === "DOWNTREND") {
    const entry = H2;
    const stopLoss = H2 + (H2 - L2) * 0.25;
    const risk = stopLoss - entry;

    trade = {
      direction: "SELL",
      entry: +entry.toFixed(2),
      stopLoss: +stopLoss.toFixed(2),
      takeProfit1: +L2.toFixed(2),
      takeProfit2: +(entry - risk * 2).toFixed(2),
      rr: "1:2"
    };
  }

  /* ---------------------------------------
     FINAL RETURN
  --------------------------------------- */
  return {
    structure,
    trend,
    strength,
    confidence,
    angle,
    slope: +slope.toFixed(4),
    swings: { highs: swingHighs, lows: swingLows },
    trade
  };
};


export function retestStrategy(data = []) {

  /* ---------- Helpers ---------- */
  const body = c => Math.abs(c.close_price - c.open_price);
  const range = c => c.high_price - c.low_price;
  const upperWick = c => c.high_price - Math.max(c.open_price, c.close_price);
  const lowerWick = c => Math.min(c.open_price, c.close_price) - c.low_price;

  if (!Array.isArray(data) || data.length < 20) {
    return { signal: 'NO_TRADE', reason: 'INSUFFICIENT_DATA', type: 'NO' };
  }

  /* ---------- Sort & Slice ---------- */
  const candles = [...data]
    .sort((a, b) => new Date(a.delivery_date) - new Date(b.delivery_date))
    .slice(-20);

  /* ---------- Build Structure (first 14 candles) ---------- */
  const structure = candles.slice(0, 12);
  const highs = structure.map(c => c.high_price);
  const lows  = structure.map(c => c.low_price);

  const resistance = Math.max(...highs);
  const support    = Math.min(...lows);

  /* ---------- Breakout Detection (last 6 candles only) ---------- */
  let breakoutIndex = null;
  let direction = null;

  for (let i = 14; i < candles.length; i++) {
    const c = candles[i];

    const strongBreak =
      body(c) / range(c) >= 0.6;

    if (c.close_price > resistance && strongBreak) {
      breakoutIndex = i;
      direction = 'BULLISH';
      break;
    }

    if (c.close_price < support && strongBreak) {
      breakoutIndex = i;
      direction = 'BEARISH';
      break;
    }
  }

  if (breakoutIndex === null) {
    return { signal: 'NO_TRADE', reason: 'NO_BREAKOUT', type: 'NO' };
  }

  /* ---------- Retest Detection (max 6 candles after breakout) ---------- */
  const maxRetestBars = 6;
  const tolerance = 0.015; // 1.5%

  for (
    let i = breakoutIndex + 1;
    i <= Math.min(breakoutIndex + maxRetestBars, candles.length - 1);
    i++
  ) {
    const c = candles[i];
    if (range(c) === 0) continue;

    const strongBody = body(c) / range(c) >= 0.45;

    /* ===== Bullish Retest ===== */
    if (direction === 'BULLISH') {
      const nearLevel =
        Math.abs(c.low_price - resistance) / resistance <= tolerance;

      const bullish = c.close_price > c.open_price;
      const rejection = lowerWick(c) >= body(c) * 0.5;

      if (nearLevel && bullish && strongBody && rejection) {
        const sl = Math.min(...candles.slice(breakoutIndex, i + 1).map(x => x.low_price));

        return {
          signal: 'BUY',
          entry: c.close_price,
          sl,
          tp: c.close_price + (c.close_price - sl) * 2,
          type: 'CONFIRMED_BULLISH_RETEST',
          breakout_index: breakoutIndex,
          retest_index: i
        };
      }
    }

    /* ===== Bearish Retest ===== */
    if (direction === 'BEARISH') {
      const nearLevel =
        Math.abs(c.high_price - support) / support <= tolerance;

      const bearish = c.close_price < c.open_price;
      const rejection = upperWick(c) >= body(c) * 0.5;

      if (nearLevel && bearish && strongBody && rejection) {
        const sl = Math.max(...candles.slice(breakoutIndex, i + 1).map(x => x.high_price));

        return {
          signal: 'SELL',
          entry: c.close_price,
          sl,
          tp: c.close_price - (sl - c.close_price) * 2,
          type: 'CONFIRMED_BEARISH_RETEST',
          breakout_index: breakoutIndex,
          retest_index: i
        };
      }
    }
  }

  return { signal: 'NO_TRADE', reason: 'NO_VALID_RETEST', type: 'NO' };
}


export function insideRangeGap(prev, today) {
  // ---------- Safety checks ----------
  if (!prev || !today) return 'INVALID_DATA';

  const prevOpen  = Number(prev.open_price);
  const prevClose = Number(prev.close_price);
  const prevHigh  = Number(prev.high_price);
  const prevLow   = Number(prev.low_price);
  const todayOpen = Number(today.open_price);

  if (!prevOpen || !prevClose || !prevHigh || !prevLow || !todayOpen) {
    return 'INVALID_DATA';
  }

  // ---------- Previous day body % ----------
  const prevBodyPercent =
    Math.abs(prevClose - prevOpen) / prevOpen * 100;

  // Require strong previous candle (≥ 5%)
  if (prevBodyPercent < 5) {
    return 'WEAK_PREVIOUS_DAY';
  }

  // ---------- Inside range check ----------
  const insideRange =
    todayOpen < prevHigh && todayOpen > prevLow;

  if (!insideRange) {
    return 'OUTSIDE_RANGE';
  }

  // ---------- Gap % ----------
  const gapPercent =
    Math.abs(todayOpen - prevClose) / prevClose * 100;

  // Ignore weak gaps (< 1%)
  if (gapPercent < 1) {
    return 'WEAK_GAP';
  }

  // ---------- Gap direction ----------
  if (todayOpen > prevClose) {
    return 'GAP_UP_INSIDE_RANGE';
  }

  if (todayOpen < prevClose) {
    return 'GAP_DOWN_INSIDE_RANGE';
  }

  return 'NEUTRAL';
}

/*
 OLD SCRIPT

export function detectWeeklyFakeBreakdown(data = []) {
  if (!Array.isArray(data) || data.length < 20) {
    return { signal: 'NO_TRADE', reason: 'INSUFFICIENT_DATA' };
  }

  // ---------- Sort oldest → newest ----------
  const sorted = [...data].sort(
    (a, b) => new Date(a.delivery_date) - new Date(b.delivery_date)
  );

  // ---------- Build Weekly Candles ----------
  const weeks = [];

  sorted.forEach(candle => {
    const d = new Date(candle.delivery_date);

    // ISO week key: YYYY-WW
    const weekKey = `${d.getFullYear()}-${getISOWeek(d)}`;

    let week = weeks.find(w => w.week === weekKey);

    if (!week) {
      week = {
        week: weekKey,
        start_date: candle.delivery_date,
        open: Number(candle.open_price),
        high: Number(candle.high_price),
        low: Number(candle.low_price),
        close: Number(candle.close_price)
      };
      weeks.push(week);
    } else {
      week.high = Math.max(week.high, Number(candle.high_price));
      week.low = Math.min(week.low, Number(candle.low_price));
      week.close = Number(candle.close_price);
    }
  });

  if (weeks.length < 2) {
    return { signal: 'NO_TRADE', reason: 'NOT_ENOUGH_WEEKS' };
  }

  // ---------- Last completed week ----------
  const lastWeek = weeks[weeks.length - 2];
  const currentWeek = weeks[weeks.length - 1];

  const lastWeekHigh = lastWeek.high;
  const lastWeekLow = lastWeek.low;

  // ---------- Weekly structure ----------
  const lastWeekBody = Math.abs(lastWeek.close - lastWeek.open);
  const lastWeekRange = lastWeek.high - lastWeek.low;

  const lastWeekBodyPct =
    lastWeekRange > 0 ? (lastWeekBody / lastWeekRange) * 100 : 0;

  let structure = 'RANGE';
  if (lastWeek.close > lastWeek.open && lastWeekBodyPct >= 40) {
    structure = 'BULLISH';
  } else if (lastWeek.close < lastWeek.open && lastWeekBodyPct >= 40) {
    structure = 'BEARISH';
  }

  // ---------- Fake Breakdown Logic ----------
  const fakeBreakdown =
    currentWeek.low < lastWeekLow &&
    currentWeek.close > lastWeekLow;

  if (!fakeBreakdown) {
    return {
      signal: 'NO_TRADE',
      reason: 'NO_FAKE_BREAKDOWN',
      lastWeekHigh,
      lastWeekLow,
      structure
    };
  }

  // ---------- Strength filter (optional but recommended) ----------
  const strength =
    currentWeek.close > currentWeek.open ? 'STRONG' : 'MODERATE';

  return {
    signal: 'BUY',
    type: 'WEEKLY_FAKE_BREAKDOWN',
    structure,
    entry: currentWeek.close,
    support: lastWeekLow,
    lastWeekHigh,
    lastWeekLow,
    strength
  };
}


function getISOWeek(date) {
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  const dayNum = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  return Math.ceil((((d - yearStart) / 86400000) + 1) / 7);
}


*/

export function weeklyFakeBreakdownScanner(data = []) {
  if (!Array.isArray(data) || data.length < 20) {
    return { signal: 'NO_TRADE', reason: 'INSUFFICIENT_DATA' };
  }

  // ---------- Sort oldest → newest ----------
  const sorted = [...data].sort(
    (a, b) => new Date(a.delivery_date) - new Date(b.delivery_date)
  );

  // ---------- Build Weekly Candles ----------
  const weeks = [];

  sorted.forEach(c => {
    const date = new Date(c.delivery_date);
    const weekKey = `${date.getFullYear()}-${getISOWeek(date)}`;

    let week = weeks.find(w => w.week === weekKey);

    if (!week) {
      week = {
        week: weekKey,
        open: Number(c.open_price),
        high: Number(c.high_price),
        low: Number(c.low_price),
        close: Number(c.close_price),
        days: []
      };
      weeks.push(week);
    } else {
      week.high = Math.max(week.high, Number(c.high_price));
      week.low  = Math.min(week.low, Number(c.low_price));
      week.close = Number(c.close_price);
    }

    week.days.push({
      ...c,
      dayOfWeek: new Date(c.delivery_date).getDay() // 1=Mon ... 4=Thu
    });
  });

  if (weeks.length < 2) {
    return { signal: 'NO_TRADE', reason: 'NOT_ENOUGH_WEEKS' };
  }

  const lastWeek = weeks[weeks.length - 2];
  const currentWeek = weeks[weeks.length - 1];

  // ---------- LAST WEEK SOLID BEARISH CHECK ----------
  const range = lastWeek.high - lastWeek.low;

  if (range <= 0) {
    return { signal: 'NO_TRADE', reason: 'INVALID_WEEK_RANGE' };
  }

  const body = Math.abs(lastWeek.open - lastWeek.close);
  const upperWick = lastWeek.high - Math.max(lastWeek.open, lastWeek.close);
  const lowerWick = Math.min(lastWeek.open, lastWeek.close) - lastWeek.low;

  const bodyPct = (body / range) * 100;
  const wickPct = ((upperWick + lowerWick) / range) * 100;

  const lastWeekBearish =
    lastWeek.close < lastWeek.open &&
    bodyPct >= 80 &&
    wickPct <= 20;

  if (!lastWeekBearish) {
    return {
      signal: 'NO_TRADE',
      reason: 'LAST_WEEK_NOT_SOLID_BEARISH',
      bodyPct: Number(bodyPct.toFixed(2)),
      wickPct: Number(wickPct.toFixed(2))
    };
  }

  const lastWeekLow = lastWeek.low;
  const lastWeekHigh = lastWeek.high;

  // ---------- Only Mon–Thu ----------
  const validDays = currentWeek.days.filter(
    d => d.dayOfWeek >= 1 && d.dayOfWeek <= 4
  );

  // ---------- Fake Breakdown + Retest Logic ----------
  for (let i = 0; i < validDays.length; i++) {
    const day = validDays[i];

    // 1️⃣ Breakdown below last week low
    if (Number(day.low_price) < lastWeekLow) {
      const breakdownDay = day;

      // 2️⃣ Same day or next day bullish retest
      const candidates = [validDays[i], validDays[i + 1]].filter(Boolean);

      const recoveryDay = candidates.find(
        d => Number(d.close_price) > lastWeekLow
      );

      if (!recoveryDay) continue;

      // 3️⃣ Remove if last week high already hit
      const hitWeeklyHigh = validDays
        .slice(i)
        .some(d => Number(d.high_price) >= lastWeekHigh);

      if (hitWeeklyHigh) {
        return {
          signal: 'REMOVE',
          reason: 'LAST_WEEK_HIGH_ALREADY_HIT',
          lastWeekHigh,
          lastWeekLow
        };
      }

      // ✅ BUY SIGNAL
      return {
        signal: 'BUY',
        type: 'WEEKLY_FAKE_BREAKDOWN_RETEST',
        entry: Number(recoveryDay.close_price),
        support: lastWeekLow,
        resistance: lastWeekHigh,
        breakdown_date: breakdownDay.delivery_date,
        recovery_date: recoveryDay.delivery_date,
        lastWeek: {
          high: lastWeekHigh,
          low: lastWeekLow,
          bodyPct: Number(bodyPct.toFixed(2)),
          wickPct: Number(wickPct.toFixed(2))
        }
      };
    }
  }

  return {
    signal: 'NO_TRADE',
    reason: 'NO_VALID_FAKE_BREAKDOWN',
    lastWeekLow,
    lastWeekHigh
  };
}

/* ---------- ISO Week Helper ---------- */
function getISOWeek(date) {
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  const dayNum = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  return Math.ceil((((d - yearStart) / 86400000) + 1) / 7);
}

// --------------------------------------------------------------

export function detectSwingLiquidityInducement(data = [], lookback = 20) {
  if (!Array.isArray(data) || data.length < lookback + 2) {
    return { signal: 'NO_TRADE', reason: 'INSUFFICIENT_DATA' };
  }

  // Sort oldest → newest
  const sorted = [...data].sort(
    (a, b) => new Date(a.delivery_date) - new Date(b.delivery_date)
  );

  const recent = sorted.slice(-lookback - 2);


  // Swing levels
  const highs = recent.slice(0, lookback).map(d => Number(d.high_price));
  const lows  = recent.slice(0, lookback).map(d => Number(d.low_price));

  const swingHigh = Math.max(...highs);
  const swingLow  = Math.min(...lows);


  const swingHighIndex = highs.lastIndexOf(swingHigh);
  const swingLowIndex  = lows.lastIndexOf(swingLow);

  // const swingHighDate = recent[swingHighIndex].delivery_date;
  // const swingLowDate  = recent[swingLowIndex].delivery_date;

  // Check last candle
  const candle = recent[recent.length - 1];

  // 🔻 Sell-side liquidity taken (Bullish)
  const FVG = detectFVG(recent.slice(0, swingHighIndex))
  
  if (
    swingHighIndex >= 12 &&
    FVG.bullishCount >= 1 &&
    Number(candle.low_price) < swingLow &&
    Number(candle.close_price) > swingLow  
  ) {
      const entry = Number(candle.close_price);
      const support = Number(swingLow);
      const target = Number(swingHigh);

      const targetPer = ((target - entry) / entry) * 100;
      const slPer = ((entry - support) / entry) * 100;

      return {
            signal: 'BUY',
            type: 'SWING_LIQUIDITY_GRAB',
            liquidity: 'SELL_SIDE',
            entry,
            support,
            target,
            targetPer: Number(targetPer.toFixed(2)), // %
            slPer: Number(slPer.toFixed(2)),         // %
            date: candle.delivery_date,
            // swingHighDate,
            // swingLowDate,
            FVG,
      };
  }

  // 🔺 Buy-side liquidity taken (Bearish)
  if (
    Number(candle.high_price) > swingHigh &&
    Number(candle.close_price) < swingHigh
  ) {
        const entry = Number(candle.close_price);
        const resistance = Number(swingHigh);
        const target = Number(swingLow);

        const targetPer = ((entry - target) / entry) * 100;
        const slPer = ((resistance - entry) / entry) * 100;

        return {
            signal: 'SELL',
            type: 'SWING_LIQUIDITY_GRAB',
            liquidity: 'BUY_SIDE',
            entry,
            resistance,
            target,
            targetPer: Number(targetPer.toFixed(2)), // %
            slPer: Number(slPer.toFixed(2)),         // %
            date: candle.delivery_date,
            FVG,
        };

  }

  return {
    signal: 'NO_TRADE',
    reason: 'NO_LIQUIDITY_INDUCEMENT',
    swingHigh,
    swingLow
  };
}


export function detectWeeklyLiquidityInducement(data = []) {
  if (!Array.isArray(data) || data.length < 10) {
    return { signal: 'NO_TRADE', reason: 'INSUFFICIENT_DATA' };
  }

  // Sort oldest → newest
  const sorted = [...data].sort(
    (a, b) => new Date(a.delivery_date) - new Date(b.delivery_date)
  );

  // ---------- Build Weekly Candles ----------
  const weeks = [];

  sorted.forEach(c => {
    const date = new Date(c.delivery_date);
    const weekKey = `${date.getFullYear()}-${getISOWeek(date)}`;

    let week = weeks.find(w => w.week === weekKey);

    if (!week) {
      week = {
        week: weekKey,
        high: Number(c.high_price),
        low: Number(c.low_price),
        close: Number(c.close_price),
        days: []
      };
      weeks.push(week);
    } else {
      week.high = Math.max(week.high, Number(c.high_price));
      week.low  = Math.min(week.low, Number(c.low_price));
      week.close = Number(c.close_price);
    }

    week.days.push(c);
  });

  if (weeks.length < 2) {
    return { signal: 'NO_TRADE', reason: 'NOT_ENOUGH_WEEKS' };
  }

  const lastWeek = weeks[weeks.length - 2];
  const currentWeek = weeks[weeks.length - 1];

  const lastWeekHigh = lastWeek.high;
  const lastWeekLow = lastWeek.low;

  // Check each day of current week
  for (const day of currentWeek.days) {
    // 🔻 Sell-side liquidity (Bullish)
    if (
      Number(day.low_price) < lastWeekLow &&
      Number(day.close_price) > lastWeekLow
    ) {
      return {
        signal: 'BUY',
        type: 'WEEKLY_LIQUIDITY_GRAB',
        liquidity: 'SELL_SIDE',
        entry: Number(day.close_price),
        support: lastWeekLow,
        target: lastWeekHigh,
        date: day.delivery_date
      };
    }

    // 🔺 Buy-side liquidity (Bearish)
    if (
      Number(day.high_price) > lastWeekHigh &&
      Number(day.close_price) < lastWeekHigh
    ) {
      return {
        signal: 'SELL',
        type: 'WEEKLY_LIQUIDITY_GRAB',
        liquidity: 'BUY_SIDE',
        entry: Number(day.close_price),
        resistance: lastWeekHigh,
        target: lastWeekLow,
        date: day.delivery_date
      };
    }
  }

  return {
    signal: 'NO_TRADE',
    reason: 'NO_WEEKLY_LIQUIDITY_INDUCEMENT',
    lastWeekHigh,
    lastWeekLow
  };
}


export function isFarFromEMA20(closePrice, ema20, maxDistancePercent = 10){

        closePrice = parseFloat(closePrice);
        ema20 = parseFloat(ema20);

        if (ema20 <= 0 || isNaN(ema20)) {
          return {
            ema_distance: 0,
            status: false
          };
        }

        const distancePercent = Math.abs((closePrice - ema20) * 100) / ema20;

      return {
        ema_distance: Number(distancePercent.toFixed(2)),
        status: distancePercent <= maxDistancePercent 
      };
}


export function normalize(value, min, max){
  if (max === min) return 50;
  return Number(
    (((value - min) / (max - min)) * 100).toFixed(2)
  );
};


export function calculateMomentum(stock_data) {
  /*
  |--------------------------------------------------------------------------
  | VALIDATION
  |--------------------------------------------------------------------------
  */

  if (!Array.isArray(stock_data) || stock_data.length < 15) {
    return {
      momentum_score: 0,
      momentum_type: "WEAK",
    };
  }

  /*
  |--------------------------------------------------------------------------
  | NEWEST -> OLDEST
  |
  | [0]  = Latest
  | [1]  = Yesterday
  | [4]  = 5 trading days ago
  | [9]  = 10 trading days ago
  | [14] = 15 trading days ago
  |--------------------------------------------------------------------------
  */

  const price0 = parseFloat(stock_data[0]?.close_price || 0);
  const price5 = parseFloat(stock_data[4]?.close_price || 0);
  const price10 = parseFloat(stock_data[9]?.close_price || 0);
  const price15 = parseFloat(stock_data[14]?.close_price || 0);


  /*
  |--------------------------------------------------------------------------
  | PRICE MOMENTUM
  |--------------------------------------------------------------------------
  */

  let priceMomentum5D = 0;
  let priceMomentumPrevious5D = 0;
  let priceMomentumOlder5D = 0;


  /*
  | Recent 5D
  */

  if (price5 > 0) {
    priceMomentum5D =
      ((price0 - price5) / price5) * 100;
  }


  /*
  | Previous 5D
  */

  if (price10 > 0) {
    priceMomentumPrevious5D =
      ((price5 - price10) / price10) * 100;
  }


  /*
  | Older 5D
  */

  if (price15 > 0) {
    priceMomentumOlder5D =
      ((price10 - price15) / price15) * 100;
  }


  /*
  |--------------------------------------------------------------------------
  | PRICE ACCELERATION
  |--------------------------------------------------------------------------
  */

  const PriceAcceleration =
    priceMomentum5D -
    priceMomentumPrevious5D;


  const PriceAccelerationPrevious =
    priceMomentumPrevious5D -
    priceMomentumOlder5D;


  /*
  |--------------------------------------------------------------------------
  | VOLUME
  |--------------------------------------------------------------------------
  */

  let volumeRecent5 = 0;
  let volumePrevious5 = 0;
  let volumeOlder5 = 0;


  /*
  | Recent 5 days
  */

  for (let i = 0; i < 5; i++) {
    volumeRecent5 += parseFloat(
      stock_data[i]?.ttl_trd_qty || 0
    );
  }


  /*
  | Previous 5 days
  */

  for (let i = 5; i < 10; i++) {
    volumePrevious5 += parseFloat(
      stock_data[i]?.ttl_trd_qty || 0
    );
  }


  /*
  | Older 5 days
  */

  for (let i = 10; i < 15; i++) {
    volumeOlder5 += parseFloat(
      stock_data[i]?.ttl_trd_qty || 0
    );
  }


  /*
  |--------------------------------------------------------------------------
  | AVERAGE VOLUME
  |--------------------------------------------------------------------------
  */

  const avgVolumeRecent5 =
    volumeRecent5 / 5;

  const avgVolumePrevious5 =
    volumePrevious5 / 5;

  const avgVolumeOlder5 =
    volumeOlder5 / 5;


  /*
  |--------------------------------------------------------------------------
  | VOLUME MOMENTUM
  |--------------------------------------------------------------------------
  */

  let VolumeMomentum = 0;

  if (avgVolumePrevious5 > 0) {
    VolumeMomentum =
      ((avgVolumeRecent5 / avgVolumePrevious5) - 1) * 100;
  }


  /*
  |--------------------------------------------------------------------------
  | PREVIOUS VOLUME MOMENTUM
  |--------------------------------------------------------------------------
  */

  let previousVolumeMomentum = 0;

  if (avgVolumeOlder5 > 0) {
    previousVolumeMomentum =
      ((avgVolumePrevious5 / avgVolumeOlder5) - 1) * 100;
  }


  /*
  |--------------------------------------------------------------------------
  | VOLUME ACCELERATION
  |--------------------------------------------------------------------------
  */

  const VolumeAcceleration =
    VolumeMomentum -
    previousVolumeMomentum;


  /*
  |--------------------------------------------------------------------------
  | ROUND VALUES
  |--------------------------------------------------------------------------
  */

  priceMomentum5D =
    Number(priceMomentum5D.toFixed(2));

  priceMomentumPrevious5D =
    Number(priceMomentumPrevious5D.toFixed(2));

  priceMomentumOlder5D =
    Number(priceMomentumOlder5D.toFixed(2));

  const priceAcceleration =
    Number(PriceAcceleration.toFixed(2));

  const priceAccelerationPrevious =
    Number(PriceAccelerationPrevious.toFixed(2));

  const volumeMomentum =
    Number(VolumeMomentum.toFixed(2));

  const volumeAcceleration =
    Number(VolumeAcceleration.toFixed(2));


  /*
  |--------------------------------------------------------------------------
  | SCORE COMPONENTS
  |--------------------------------------------------------------------------
  */

  const priceMomentumScore = Math.min(
    100,
    Math.max(
      0,
      (priceMomentum5D / 5) * 100
    )
  );


  const priceAccelerationScore = Math.min(
    100,
    Math.max(
      0,
      (priceAcceleration / 4) * 100
    )
  );


  const volumeAccelerationScore = Math.min(
    100,
    Math.max(
      0,
      (volumeAcceleration / 40) * 100
    )
  );


  /*
  |--------------------------------------------------------------------------
  | MOMENTUM SCORE
  |--------------------------------------------------------------------------
  |
  | Price Momentum       = 40%
  | Price Acceleration   = 30%
  | Volume Acceleration  = 30%
  |--------------------------------------------------------------------------
  */

  let momentum_score =
      (priceMomentumScore * 0.40)
    + (priceAccelerationScore * 0.30)
    + (volumeAccelerationScore * 0.30);


  /*
  |--------------------------------------------------------------------------
  | NEGATIVE PRICE DIRECTION PENALTY
  |--------------------------------------------------------------------------
  */

  if (priceMomentum5D <= 0) {
    momentum_score *= 0.40;
  }


  /*
  |--------------------------------------------------------------------------
  | NEGATIVE PRICE ACCELERATION PENALTY
  |--------------------------------------------------------------------------
  */

  if (priceAcceleration < 0) {
    momentum_score *= 0.80;
  }


  /*
  |--------------------------------------------------------------------------
  | FINAL SCORE
  |--------------------------------------------------------------------------
  */

  momentum_score = Number(
    Math.max(
      0,
      Math.min(
        100,
        momentum_score
      )
    ).toFixed(2)
  );


  /*
  |--------------------------------------------------------------------------
  | MOMENTUM TYPE
  |--------------------------------------------------------------------------
  */

  let momentum_type = "WEAK";


  /*
  | WEAK
  */

  if (
    priceMomentum5D <= 0 ||
    priceAcceleration <= 0 ||
    volumeAcceleration <= 0
  ) {

    momentum_type = "WEAK";


  /*
  | STRONG
  */

  } else if (
    priceMomentum5D >= 4 &&
    priceAcceleration >= 3 &&
    volumeAcceleration >= 30 &&
    momentum_score >= 80
  ) {

    momentum_type = "STRONG";


  /*
  | UPCOMING
  */

  } else if (
    priceMomentum5D > 0 &&
    priceAcceleration >= 2 &&
    volumeAcceleration >= 10 &&
    momentum_score >= 60
  ) {

    momentum_type = "UPCOMING";


  /*
  | BUILDING
  */

  } else if (
    priceMomentum5D > 0 &&
    priceAcceleration > 0 &&
    volumeAcceleration > 0
  ) {

    momentum_type = "BUILDING";


  /*
  | DEFAULT
  */

  } else {

    momentum_type = "WEAK";
  }


  /*
  |--------------------------------------------------------------------------
  | RETURN
  |--------------------------------------------------------------------------
  */

  return {
    momentum_score,
    momentum_type,

    // Optional: useful for debugging/backtesting
    priceMomentum5D,
    priceMomentumPrevious5D,
    priceMomentumOlder5D,
    priceAcceleration,
    priceAccelerationPrevious,
    volumeMomentum,
    volumeAcceleration,
  };
}

export function getSwingSetup(item) {

    const stockData = item?.stock_data || [];

    if (stockData.length < 5) {
        return {
            eligible: false,
            setup: "INSUFFICIENT_DATA",
            score: 0
        };
    }


    // --------------------------------------------------
    // CURRENT DATA
    // --------------------------------------------------

    const current = stockData[0];

    const momentumType =
        String(current?.momentum_type || item?.momentum_type || "")
            .toUpperCase();

    const momentumScore =
        Number(current?.momentum_score ?? item?.momentum_score ?? 0);

    const overallScore =
        Number(current?.score ?? item?.score ?? 0);

    const rsi =
        Number(item?.RSI || 0);

    const adx =
        Number(item?.ADX || 0);

    const atrPer =
        Number(item?.ATR_PER || 0);

    const avgVolume =
        Number(item?.avgVolume || 0);

    const structure =
        String(item?.structure_json?.structure || "")
            .toUpperCase();


    // --------------------------------------------------
    // PRICE DATA
    // --------------------------------------------------

    const price0 =
        Number(stockData[0]?.close_price || 0);

    const price1 =
        Number(stockData[1]?.close_price || 0);

    const price2 =
        Number(stockData[2]?.close_price || 0);

    const price3 =
        Number(stockData[3]?.close_price || 0);

    const price4 =
        Number(stockData[4]?.close_price || 0);


    // --------------------------------------------------
    // PRICE RETURNS
    // --------------------------------------------------

    const return1D =
        price1 > 0
            ? ((price0 - price1) / price1) * 100
            : 0;

    const return2D =
        price2 > 0
            ? ((price0 - price2) / price2) * 100
            : 0;

    const return3D =
        price3 > 0
            ? ((price0 - price3) / price3) * 100
            : 0;

    const return5D =
        price4 > 0
            ? ((price0 - price4) / price4) * 100
            : 0;


    // --------------------------------------------------
    // MOMENTUM PERSISTENCE
    // --------------------------------------------------

    const last5 = stockData.slice(0, 5);

    const upcomingCount = last5.filter(
        x => String(x?.momentum_type || "").toUpperCase() === "UPCOMING"
    ).length;

    const strongCount = last5.filter(
        x => String(x?.momentum_type || "").toUpperCase() === "STRONG"
    ).length;

    const buildingCount = last5.filter(
        x => String(x?.momentum_type || "").toUpperCase() === "BUILDING"
    ).length;


    // --------------------------------------------------
    // FRESH MOMENTUM
    // --------------------------------------------------

    const freshMomentum =
        momentumType === "UPCOMING" ||
        momentumType === "STRONG";


    // --------------------------------------------------
    // MOMENTUM IMPROVEMENT
    // --------------------------------------------------

    const momentumImproving =
        upcomingCount >= 2 ||
        strongCount >= 2 ||
        (
            stockData[0]?.momentum_type === "UPCOMING" &&
            (
                stockData[1]?.momentum_type === "UPCOMING" ||
                stockData[1]?.momentum_type === "BUILDING"
            )
        );


    // --------------------------------------------------
    // PRICE TREND
    // --------------------------------------------------

    const priceTrendPositive =
        return3D > 0 &&
        return5D > 0;


    // --------------------------------------------------
    // SHORT TERM PRICE CONFIRMATION
    // --------------------------------------------------

    const shortTermPositive =
        return2D > 0 ||
        return3D > 0;


    // --------------------------------------------------
    // VOLUME
    // --------------------------------------------------

    const volumeRecent3 =
        stockData
            .slice(0, 3)
            .reduce(
                (sum, day) =>
                    sum + Number(day?.ttl_trd_qty || 0),
                0
            ) / 3;


    const volumePrevious3 =
        stockData
            .slice(3, 6)
            .reduce(
                (sum, day) =>
                    sum + Number(day?.ttl_trd_qty || 0),
                0
            ) / 3;


    const volumeMomentum =
        volumePrevious3 > 0
            ? ((volumeRecent3 / volumePrevious3) - 1) * 100
            : 0;


    // --------------------------------------------------
    // VOLUME CONFIRMATION
    // --------------------------------------------------

    const volumePositive =
        volumeMomentum > 0;


    // --------------------------------------------------
    // STRUCTURE
    // --------------------------------------------------

    const bullishStructure =
        structure === "UPTREND";


    const rangeStructure =
        structure === "RANGE";


    // --------------------------------------------------
    // RSI
    // --------------------------------------------------

    const rsiHealthy =
        rsi >= 50 &&
        rsi <= 72;


    const rsiTooHigh =
        rsi > 78;


    // --------------------------------------------------
    // ADX
    // --------------------------------------------------

    const trendStrength =
        adx >= 25;


    // --------------------------------------------------
    // SWING SCORE
    // --------------------------------------------------

    let swingScore = 0;


    // Momentum
    if (momentumScore >= 80) {
        swingScore += 25;
    } else if (momentumScore >= 70) {
        swingScore += 20;
    } else if (momentumScore >= 60) {
        swingScore += 15;
    }


    // Momentum type
    if (momentumType === "UPCOMING") {
        swingScore += 15;
    }

    if (momentumType === "STRONG") {
        swingScore += 12;
    }

    if (momentumType === "BUILDING") {
        swingScore += 7;
    }


    // Persistence
    if (upcomingCount >= 3) {
        swingScore += 10;
    } else if (upcomingCount >= 2) {
        swingScore += 7;
    }

    if (strongCount >= 3) {
        swingScore += 8;
    } else if (strongCount >= 2) {
        swingScore += 5;
    }


    // Price trend
    if (return3D > 0) {
        swingScore += 8;
    }

    if (return5D > 0) {
        swingScore += 7;
    }


    // Volume
    if (volumeMomentum > 30) {
        swingScore += 8;
    } else if (volumeMomentum > 10) {
        swingScore += 6;
    } else if (volumeMomentum > 0) {
        swingScore += 3;
    }


    // Structure
    if (bullishStructure) {
        swingScore += 10;
    } else if (rangeStructure) {
        swingScore += 3;
    }


    // ADX
    if (adx >= 30) {
        swingScore += 5;
    } else if (adx >= 25) {
        swingScore += 3;
    }


    // RSI
    if (rsiHealthy) {
        swingScore += 5;
    }

    if (rsiTooHigh) {
        swingScore -= 8;
    }


    // Overextended price
    if (return5D > 15) {
        swingScore -= 10;
    }


    swingScore = Math.max(
        0,
        Math.min(100, swingScore)
    );


    // --------------------------------------------------
    // SETUP CLASSIFICATION
    // --------------------------------------------------

    let setup = "AVOID";


    if (
        freshMomentum &&
        momentumScore >= 70 &&
        bullishStructure &&
        trendStrength &&
        priceTrendPositive &&
        volumePositive &&
        rsi >= 50 &&
        rsi < 75 &&
        swingScore >= 75
    ) {

        setup = "STRONG_SWING";


    } else if (
        freshMomentum &&
        momentumScore >= 60 &&
        (
            bullishStructure ||
            rangeStructure
        ) &&
        trendStrength &&
        shortTermPositive &&
        swingScore >= 60
    ) {

        setup = "WATCH";


    } else if (
        (
            momentumType === "BUILDING" ||
            momentumType === "UPCOMING"
        ) &&
        shortTermPositive
    ) {

        setup = "BUILDING";


    } else {

        setup = "AVOID";
    }


    // --------------------------------------------------
    // RETURN
    // --------------------------------------------------

    return {
        eligible:
            setup === "STRONG_SWING" ||
            setup === "WATCH",

        setup,

        swingScore: Number(swingScore.toFixed(2)),

        momentumType,
        momentumScore,

        return1D: Number(return1D.toFixed(2)),
        return2D: Number(return2D.toFixed(2)),
        return3D: Number(return3D.toFixed(2)),
        return5D: Number(return5D.toFixed(2)),

        upcomingCount,
        strongCount,
        buildingCount,

        volumeMomentum: Number(
            volumeMomentum.toFixed(2)
        ),

        structure,
        adx,
        rsi,
        atrPer,
        avgVolume
    };
}